import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { STATUS_CODE } from '@/constants/enum';
import { useUpdateDiscountCodeMutation } from '@/redux/query';

import { Banner, BlockStack, Box, FormLayout, Modal, TextField } from '@shopify/polaris';
import { useEffect, useState } from 'react';

interface DiscountModalProps {
  handleClose: () => void;
  isOpenDiscountModal: boolean;
  currentDiscountCode: string;
}

export const DiscountModal = ({ isOpenDiscountModal, handleClose, currentDiscountCode }: DiscountModalProps) => {
  const [discountCode, setDiscountCode] = useState<string>('');
  const [validate, setValidate] = useState({
    isValid: false,
    isError: false,
    message: 'Your discount code is invalid',
  });
  const [updateDiscountCode, { isLoading: isLoadingUpdateDiscountCode }] = useUpdateDiscountCodeMutation();

  const handleChangeDiscountCode = (value: string) => {
    setValidate((prev) => ({ ...prev, isError: false }));
    setDiscountCode(value);
  };

  useEffect(() => {
    setDiscountCode(currentDiscountCode || '');
  }, [currentDiscountCode]);

  return (
    <Modal
      open={isOpenDiscountModal}
      title="Apply discount code"
      onClose={handleClose}
      primaryAction={{
        content: 'Apply',
        onAction: () => {
          updateDiscountCode({ shop: config.shop, discountCode: discountCode.trim() })
            .unwrap()
            .then((res) => {
              if (res.statusCode === STATUS_CODE.OK || res.statusCode === STATUS_CODE.CREATED) {
                setValidate((prev) => ({ ...prev, isValid: true, isError: false }));
              } else
                setValidate((prev) => ({
                  ...prev,
                  isValid: false,
                  isError: true,
                  message: res?.message || 'Your discount code is invalid',
                }));
            })
            .catch(() => {
              setValidate((prev) => ({ ...prev, isValid: false, isError: true, message: 'Your discount code is invalid' }));
            });
        },
        loading: isLoadingUpdateDiscountCode,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose,
        },
      ]}
    >
      <Box padding="400">
        <BlockStack gap="400">
          <FormLayout>
            <FormLayout.Group>
              <TextField
                label="Discount code"
                value={discountCode}
                onChange={(value) => handleChangeDiscountCode(value)}
                onClearButtonClick={() => handleChangeDiscountCode('')}
                clearButton
                autoComplete="off"
                autoFocus
                requiredIndicator
                error={validate.isError && validate.message}
              />
            </FormLayout.Group>
          </FormLayout>
          {validate.isValid && (
            <Banner title="Discount code is valid" tone="success" onDismiss={() => {}}>
              <RegularText>Congratulations! Your discount code is valid</RegularText>
            </Banner>
          )}
        </BlockStack>
      </Box>
    </Modal>
  );
};
